import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, UIMessage } from 'ai';
import { produce } from 'immer';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';

import { useApiClient } from '@eluve/api-client-provider';
import { getEndpointUrl } from '@eluve/api-contract';
import { Citation } from '@eluve/blocks';
import { ChatThread, StartingChatMessage } from '@eluve/blocks';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { Chat } from './Chat';

interface ChatMessageMetadata {
  citations?: Citation[];
}

type UIMessageRole = 'user' | 'system' | 'assistant';

interface ApiMessageWithAnnotations {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system' | 'data';
  annotations?: ChatMessageMetadata[];
}

interface ChatConfig {
  /**
   * Optional when provided, enables patient-specific features
   */
  patientId?: string;
  /**
   * Starting common questions / chat templates
   */
  startingMessages: StartingChatMessage[];
}

interface ChatComponentProps {
  tenantId: string;
  baseUrl: string;
  config: ChatConfig;
}

export const ChatComponent = ({
  tenantId,
  baseUrl,
  config,
}: ChatComponentProps) => {
  const logger = useNamedLogger('chat');
  const analytics = useAnalytics('chat');
  const userId = useUserIdFromSession();
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [isCreatingThread, setIsCreatingThread] = useState<boolean>(false);
  const apiClient = useApiClient();

  const createThread = useCallback(async () => {
    const result = await apiClient.mastra.createChatThread({
      params: { tenantId },
      body: config.patientId ? { patientId: config.patientId } : {},
    });

    if (result.status === 201) {
      const { threadId } = result.body;
      setCurrentThreadId(threadId);

      const newThread = {
        id: threadId,
        title: `New Thread ${new Date().toISOString()}`,
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return { threadId, newThread };
    }
    return null;
  }, [apiClient, tenantId, config.patientId]);

  useMount(async () => {
    const result = await apiClient.mastra.getChatThreads({
      params: { tenantId },
      query: config.patientId ? { patientId: config.patientId } : {},
    });
    if (result.status === 200) {
      const { threads } = result.body;
      setChatThreads(threads);

      if (threads.length === 0) {
        const threadResult = await createThread();
        if (threadResult) {
          setChatThreads([threadResult.newThread]);
        }
      }
    }
  });

  const loadThreadTitle = useCallback(
    async (threadId: string) => {
      const result = await apiClient.mastra.getChatThreadTitle({
        params: { tenantId, threadId },
      });

      if (result.status === 200) {
        const { title } = result.body;
        setChatThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId ? { ...thread, title } : thread,
          ),
        );
      }
    },
    [apiClient, tenantId],
  );

  const [input, setInput] = useState('');

  // Use a ref to ensure prepareSendMessagesRequest always reads the latest threadId
  const currentThreadIdRef = useRef(currentThreadId);
  currentThreadIdRef.current = currentThreadId;

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: getEndpointUrl(baseUrl, 'chatInsightsWithCitations', {
          params: { tenantId },
        }),
        credentials: 'include',
        prepareSendMessagesRequest: ({ messages: chatMessages }) => {
          const lastMessage =
            chatMessages.length > 0
              ? chatMessages[chatMessages.length - 1]
              : null;

          // Extract text content from the message parts
          const messageContent = lastMessage
            ? {
                content: lastMessage.parts
                  .filter(
                    (p): p is { type: 'text'; text: string } =>
                      p.type === 'text',
                  )
                  .map((p) => p.text)
                  .join(''),
              }
            : null;

          return {
            body: {
              message: messageContent,
              threadId: currentThreadIdRef.current,
              ...(config.patientId && { patientId: config.patientId }),
            },
          };
        },
      }),
    [baseUrl, tenantId, config.patientId],
  );

  const { messages, status, setMessages, sendMessage } = useChat<
    UIMessage<ChatMessageMetadata>
  >({
    transport,
    onFinish: async () => {
      const threadId = currentThreadIdRef.current;
      if (!threadId) {
        return;
      }

      // Auto generate title after first complete exchange
      if (messages.length < 2) {
        try {
          await apiClient.mastra.updateThreadTitle({
            params: { tenantId, threadId },
            query: config.patientId ? { patientId: config.patientId } : {},
            body: { autoGenerate: true },
          });
        } catch (error) {
          logger.warn('Failed to auto-generate thread title:', {
            threadId,
            error,
          });
        }
      }
      loadThreadTitle(threadId);
    },
  });

  const loadThreadMessages = async (threadId: string) => {
    const result = await apiClient.mastra.getChatThreadMessages({
      params: { tenantId, threadId },
      query: {
        includeSources: 'true',
        ...(config.patientId && { patientId: config.patientId }),
      },
    });
    if (result.status === 200) {
      const { messages: loadedMessages } = result.body;
      // Cast to include annotations which are added by the controller when includeSources is true
      const messagesWithAnnotations =
        loadedMessages as unknown as ApiMessageWithAnnotations[];
      // Convert API messages to UIMessage format with parts
      const uiMessages = messagesWithAnnotations
        .filter(
          (msg): msg is ApiMessageWithAnnotations & { role: UIMessageRole } =>
            msg.role !== 'data',
        )
        .map((msg) => ({
          id: msg.id,
          role: msg.role,
          parts: [{ type: 'text' as const, text: msg.content || '' }],
          metadata: (msg.annotations?.[0] ?? {}) as ChatMessageMetadata,
        }));
      setMessages(uiMessages);
    }
  };

  const formattedMessages = messages.map((msg) => {
    // Extract text content from parts array
    const content = msg.parts
      .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
      .map((p) => p.text)
      .join('');

    // Extract citations from metadata
    const citations = msg.metadata?.citations ?? [];

    return {
      id: msg.id,
      content,
      role:
        msg.role === 'user' ? 'user' : ('assistant' as 'user' | 'assistant'),
      citations,
    };
  });

  const consolidatedChatThreads = produce(chatThreads, (draft) => {
    if (!currentThreadId) {
      return;
    }

    const existingThreadIndex = draft.findIndex(
      (thread) => thread.id === currentThreadId,
    );

    const existingThread = draft[existingThreadIndex];

    if (!existingThread) {
      const createdAt = new Date().toISOString();
      const newThread = {
        id: currentThreadId,
        title: `New Thread ${createdAt}`,
        messages: formattedMessages,
        createdAt: createdAt,
        updatedAt: createdAt,
      };

      draft.push(newThread);
    } else {
      existingThread.messages = formattedMessages;
    }
  });

  const createNewThread = async () => {
    if (isCreatingThread) return;

    analytics.capture('chat_new_thread', {
      tenantId,
      userId,
      patientId: config.patientId,
      previousThreadId: currentThreadId,
    });

    setIsCreatingThread(true);
    try {
      const threadResult = await createThread();
      if (threadResult) {
        setMessages([]);
        setChatThreads((prev) => [threadResult.newThread, ...prev]);
      }
    } finally {
      setIsCreatingThread(false);
    }
  };

  const handleMessageSend = (content?: string) => {
    const messageText = content || input;
    if (messageText.trim()) {
      sendMessage({ text: messageText });
      setInput('');
    }
  };

  const handleThreadTitleUpdate = async (
    threadId: string,
    newTitle: string,
  ) => {
    newTitle = newTitle.trim();

    if (!threadId || !newTitle) {
      return;
    }

    const thread = chatThreads.find((t) => t.id === threadId);
    const previousTitle = thread?.title ?? '';

    try {
      await apiClient.mastra.updateThreadTitle({
        params: { tenantId, threadId },
        query: config.patientId ? { patientId: config.patientId } : {},
        body: { title: newTitle },
      });

      analytics.capture('chat_thread_renamed', {
        tenantId,
        userId,
        patientId: config.patientId,
        threadId,
        previousTitle,
        newTitle,
      });

      setChatThreads((prev) =>
        prev.map((thread) =>
          thread.id === threadId ? { ...thread, title: newTitle } : thread,
        ),
      );
    } catch (error) {
      logger.warn('Failed to update thread title:', {
        threadId,
        error,
      });
    }
  };

  const handleSelectThread = (threadId: string | null) => {
    if (threadId === null) {
      analytics.capture('chat_history_clicked', {
        tenantId,
        userId,
        patientId: config.patientId,
        currentThreadId,
      });
    } else {
      analytics.capture('chat_thread_selected', {
        tenantId,
        userId,
        patientId: config.patientId,
        threadId,
      });
    }

    setCurrentThreadId(threadId);
    // Convert stored messages to UIMessage format with parts
    const storedMessages =
      chatThreads.find((c) => c.id === threadId)?.messages ?? [];
    const uiMessages = storedMessages
      .filter(
        (msg): msg is typeof msg & { role: UIMessageRole } =>
          msg.role !== 'data',
      )
      .map((msg) => ({
        id: msg.id,
        role: msg.role,
        parts: [{ type: 'text' as const, text: msg.content || '' }],
        metadata: { citations: msg.citations ?? [] } as ChatMessageMetadata,
      }));
    setMessages(uiMessages);
    if (threadId) {
      loadThreadMessages(threadId);
    }
  };

  return (
    <Chat
      currentThreadId={currentThreadId}
      draftMessage={input}
      status={status}
      onCreateThread={createNewThread}
      onMessageSend={handleMessageSend}
      onSelectThread={handleSelectThread}
      setDraftMessage={setInput}
      startingMessages={config.startingMessages}
      threads={consolidatedChatThreads}
      onThreadTitleUpdate={handleThreadTitleUpdate}
    />
  );
};

export type { ChatConfig, ChatComponentProps };
